import { computed, ref, watch } from 'vue'
import { defineStore } from 'pinia'
import { hidManager } from '@/api/device/hid'
import {
  buildEqCurveCommands,
  buildEqLoginStatusCommand,
  buildEqModeCommand,
  buildEqSinglePointCommand,
  type EqDataPoint,
} from '@/api/device/commands'
import type { EqMode, EqPresetData } from '@/api/eq'
import {
  addEqPreset,
  getEqList,
  pinEqPreset,
  removeEqPreset,
  renameEqPreset,
  resetEqPreset,
  setLastEqPreset,
  updateEqPoint,
} from '@/api/eq'
import { useUserStore } from './user'

export const EQ_DISPLAY_FREQUENCIES = [
  '20Hz', '60Hz', '120Hz', '250Hz', '500Hz', '800Hz', '1K',
  '2K', '4K', '6K', '8K', '10K', '12K', '20K',
] as const

const PROTOCOL_FREQUENCIES = [
  '25HZ', '50HZ', '75HZ', '150HZ', '300HZ', '600HZ', '1K',
  '2K', '3K', '6K', '8K', '10K', '12K', '16K',
]
const DEFAULT_Q = '0.75'
const MAX_CUSTOM_PRESETS = 20
const MODE_TO_HARDWARE: Record<EqMode, number> = { game: 0, movie: 1, music: 2 }
const HARDWARE_TO_MODE: EqMode[] = ['game', 'movie', 'music']

function makePreset(id: string, name: string, gains: number[], qValues: number[]): EqPresetData {
  return {
    id,
    name,
    datapoint: PROTOCOL_FREQUENCIES.map((freq, index) => ({
      fid: index + 1,
      freq,
      gain: gains[index] ?? 0,
      q: String(qValues[index] ?? DEFAULT_Q),
    })),
  }
}

// 内置值与 oldcode/js/eq_setting.js 完全一致。
const SYSTEM_PRESETS: Record<EqMode, EqPresetData[]> = {
  game: [
    makePreset('TacticalFPS', '战术竞技射击', [-5, -3, -1, 0, -4, 2, 6, 5, 4, 3, 2, 1, 0, 0], [1, 1.2, 1.4, .6, 1.8, 1.5, 1.8, 2, 2.5, 2, 1.5, 1.2, 1, .75]),
    makePreset('BattleRoyale', '大逃杀', [3.5, 4, 2, 3, -3.5, -1.5, 3, 4, 2, 4, 3, 2, 0, 0], [1.2, 1.4, 1.6, .7, 1.5, 1.2, 1.5, 1.8, 2, 2.2, 2, 1.5, 1, .75]),
    makePreset('MilitaryShooter', '军事射击', [3, 3, 1, 5, -4, 0, 2, 4, 3, 5, 4, 2, 0, -1], [1, 1.2, 1.4, .6, 1.8, 1.5, 1.8, 2, 2.5, 2, 1.5, 1.2, 1, .75]),
    makePreset('ImmersiveRPG', '沉浸式角色扮演', [3, 4, 3, 4, -1.5, 0, 2, 1, -1, 1, 3, 3, 2, 0], [1, 1.2, 1.4, .7, 1.5, 1.2, 1.5, 1.8, 2, 2, 1.5, 1.2, 1, .75]),
  ],
  music: [
    makePreset('Pop Mode', '流行歌曲', [4, 2, 0, 3, -4, -1, 4, 3, -1, 2, 1, 4, 1, 0], [1, 1.2, 1.4, .5, 1.8, 1.2, 1.5, 2, 2.5, 1.8, 1.5, 1.2, 1, .75]),
    makePreset('Bass+', '低音加强', [3.5, 6, 4, 5, -5, -3, 0, 1, -2, 0, 0, 3, 0, 0], [1.2, 1.4, 1.6, .6, 1.8, 1.5, 1.2, 1.5, 2, 1.5, 1.2, 1, 1, .75]),
    makePreset('Vocal Focus', '清爽人声', [-3, -2, -1, 1, -4, 0, 5, 6, 4, 2, 1, 3, 1, 0], [1, 1.2, 1.4, .5, 1.8, 1.5, 1.8, 2.2, 2.5, 1.8, 1.5, 1.5, 1, .75]),
    makePreset('DJ Mode', 'DJ专用', [3, 6, 3, 2, -5, -1, 1, 2, -1, 4, 3, 5, 2, 0], [1, 1.4, 1.6, .6, 1.8, 1.5, 1.2, 1.5, 2, 2, 1.8, 1.2, 1, .75]),
  ],
  movie: [
    makePreset('Theater Surround', '影视音效', [4, 5, 4, 3, -4, -1, 4, 3, 1, 2, 3, 4, 2, 0], [1.2, 1.5, 1.6, .6, 1.8, 1.2, 1.5, 1.8, 2, 1.8, 1.5, 1.2, 1, .75]),
    makePreset('Horror Atmosphere', '恐怖氛围', [3, 3, 0, -2, -4, -1, 1, 3, 2, 5, 6, 4, 3, 1], [1, 1.4, 1.5, .8, 1.8, 1.5, 1.5, 2.2, 2.5, 2, 2.5, 1.5, 1.2, .75]),
    makePreset('Sci-Fi Space', '科幻空间', [4, 5, 3, 2, -4, -3, 1, 2, 3, 4, 5, 5, 3, 1], [1.2, 1.5, 1.6, .6, 1.8, 1.5, 1.5, 1.8, 2, 2, 2.2, 1.5, 1.2, .75]),
  ],
}

const SYSTEM_PRESET_IDS: Record<EqMode, string[]> = {
  game: SYSTEM_PRESETS.game.map(item => item.id),
  music: SYSTEM_PRESETS.music.map(item => item.id),
  movie: SYSTEM_PRESETS.movie.map(item => item.id),
}

function clonePoints(points: EqDataPoint[]): EqDataPoint[] {
  return points.map(point => ({ ...point, gain: Number(point.gain) || 0, q: String(point.q || DEFAULT_Q) }))
}

function clonePresets(presets: EqPresetData[]): EqPresetData[] {
  return presets.map(preset => ({ ...preset, datapoint: clonePoints(preset.datapoint) }))
}

function createZeroCurve(template?: EqDataPoint[]): EqDataPoint[] {
  return PROTOCOL_FREQUENCIES.map((freq, index) => ({
    fid: index + 1,
    freq: template?.[index]?.freq || freq,
    gain: 0,
    q: template?.[index]?.q || DEFAULT_Q,
  }))
}

interface EqModeState {
  enabled: boolean
  currentPreset: string
  systemPresets: EqPresetData[]
  customPresets: EqPresetData[]
  lastPreset: string | null
  pinnedPreset: string | null
}

function createModeState(mode: EqMode): EqModeState {
  return {
    enabled: false,
    currentPreset: SYSTEM_PRESETS[mode][0].id,
    systemPresets: clonePresets(SYSTEM_PRESETS[mode]),
    customPresets: [],
    lastPreset: null,
    pinnedPreset: null,
  }
}

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

export const useEqStore = defineStore('eq', () => {
  const userStore = useUserStore()
  userStore.loadFromStorage()
  const gameEq = ref<EqModeState>(createModeState('game'))
  const musicEq = ref<EqModeState>(createModeState('music'))
  const movieEq = ref<EqModeState>(createModeState('movie'))
  const activeMode = ref<EqMode>('game')
  const appliedMode = ref<EqMode | null>(null)
  const appliedPresetId = ref<string | null>(null)
  const remoteLoaded = ref<Record<EqMode, boolean>>({ game: false, music: false, movie: false })
  const apiDebounceTimers = ref<Record<string, ReturnType<typeof setTimeout>>>({})
  const lastPresetTimers = ref<Record<string, ReturnType<typeof setTimeout>>>({})
  let deviceQueue: Promise<void> = Promise.resolve()

  // “+” 新增出来的标签先作为本地草稿存在：只有退出行内编辑时才真正调用新增接口。
  // 这样点击 + 后 UI 可以立即出现，不需要等待网络；同时避免“先 add 默认名、再 rename”造成接口竞态。
  const draftPresetIds = new Set<string>()

  // 用户从 UI 主动切换 EQ 后，0x80 可能恰好回到“切换前”的旧状态。
  // 在硬件确认本次写入前短暂忽略旧回读，避免底部菜单/按钮先变新值、又闪回旧值。
  let pendingHardwareWrite: {
    enabled: boolean
    mode: EqMode
    presetId: string | null
    expiresAt: number
  } | null = null
  const EQ_WRITE_GUARD_MS = 5000

  function markPendingHardwareWrite(enabled: boolean, mode: EqMode, presetId: string | null): void {
    pendingHardwareWrite = { enabled, mode, presetId, expiresAt: Date.now() + EQ_WRITE_GUARD_MS }
  }

  // activeMode 表示用户当前正在浏览的页签，appliedMode 才表示硬件实际应用的模式。
  // 0x80 每 2 秒都会回读一次，因此只允许首次硬件同步初始化浏览页签，
  // 后续回读不能把用户刚切换到的 Music/Movie 强制拉回硬件当前模式。
  let hardwareStatusInitialized = false

  function getModeState(mode: EqMode): EqModeState {
    if (mode === 'music') return musicEq.value
    if (mode === 'movie') return movieEq.value
    return gameEq.value
  }

  function presetsForMode(mode: EqMode): EqPresetData[] {
    const state = getModeState(mode)
    return [...state.systemPresets, ...state.customPresets]
  }

  function findCustomPresetLocation(presetId: string): { mode: EqMode; state: EqModeState; preset: EqPresetData } | null {
    for (const mode of ['game', 'music', 'movie'] as EqMode[]) {
      const state = getModeState(mode)
      const preset = state.customPresets.find(item => item.id === presetId)
      if (preset) return { mode, state, preset }
    }
    return null
  }

  function isDraftPreset(presetId: string): boolean {
    return draftPresetIds.has(presetId)
  }

  const currentModeState = computed(() => getModeState(activeMode.value))
  const currentSettings = currentModeState
  const allPresets = computed(() => presetsForMode(activeMode.value))
  const currentPreset = computed(() => allPresets.value.find(preset => preset.id === currentModeState.value.currentPreset))
  const currentPresetData = computed(() => currentPreset.value?.datapoint || createZeroCurve())
  const isSystemPreset = computed(() => (presetId: string) => SYSTEM_PRESET_IDS[activeMode.value].includes(presetId))
  const canAddMoreCustom = computed(() => currentModeState.value.customPresets.length < MAX_CUSTOM_PRESETS)
  const isEnabled = computed(() => appliedMode.value !== null && !!getModeState(appliedMode.value).enabled)
  const currentMode = computed(() => activeMode.value)
  const slidersInteractive = computed(() => (
    isEnabled.value
    && appliedMode.value === activeMode.value
    && appliedPresetId.value === currentModeState.value.currentPreset
  ))

  function isLoggedIn(): boolean {
    return userStore.isLoggedIn && !!userStore.uuid
  }

  function getUuid(): string {
    return userStore.uuid || ''
  }

  function getLinktype(): string {
    return hidManager.getConnectionType() === '2.4G' ? '0' : '1'
  }

  function normalizePreset(preset: EqPresetData): EqPresetData {
    return { ...preset, datapoint: clonePoints(preset.datapoint) }
  }

  function enqueueDevice(task: () => Promise<void>): Promise<void> {
    deviceQueue = deviceQueue.then(task).catch(error => {
      console.error('[EQ] 设备命令执行失败:', error)
    })
    return deviceQueue
  }

  async function sendLoginStatus(): Promise<void> {
    await hidManager.sendData(buildEqLoginStatusCommand(isLoggedIn()), false)
  }

  async function getPositioningProtocolState(): Promise<{ enabled: boolean; tag: number }> {
    try {
      const { useChijiStore } = await import('./chiji')
      const store = useChijiStore()
      const index = store.allPresets.findIndex(item => item.id === store.currentPresetId)
      return { enabled: store.enabled, tag: Math.max(0, index) }
    } catch {
      return { enabled: false, tag: 0 }
    }
  }

  async function sendModeState(enabled: boolean, mode: EqMode, presetId: string | null): Promise<void> {
    if (!hidManager.isConnected()) return
    const positioning = await getPositioningProtocolState()
    const tag = enabled && presetId
      ? Math.max(0, presetsForMode(mode).findIndex(preset => preset.id === presetId))
      : 0
    await sendLoginStatus()
    await hidManager.sendData(buildEqModeCommand(
      enabled,
      MODE_TO_HARDWARE[mode],
      tag,
      positioning.enabled,
      positioning.tag,
    ), false)
  }

  async function sendCurve(points: EqDataPoint[], includeLogin = true): Promise<void> {
    if (!hidManager.isConnected()) return
    if (includeLogin) await sendLoginStatus()
    const commands = buildEqCurveCommands(points)
    for (let index = 0; index < commands.length; index += 1) {
      await hidManager.sendData(commands[index], false)
      if (index < commands.length - 1) await delay(200)
    }
  }

  function setActiveMode(mode: EqMode) {
    activeMode.value = mode
  }

  const switchMode = setActiveMode

  async function applyPreset(
    mode: EqMode,
    presetId: string,
    options: { persist?: boolean; syncLastPreset?: boolean } = {},
  ): Promise<void> {
    const state = getModeState(mode)
    const preset = presetsForMode(mode).find(item => item.id === presetId)
    if (!preset) return
    const persist = options.persist !== false
    const syncLastPreset = options.syncLastPreset !== false

    // 先一次性完成 UI 状态切换，再进行异步互斥/硬件下发。
    // 原先这里先 await 一键吃鸡状态，activeMode 已切换但目标 mode 仍是 disabled，
    // 会让底部 EQ 文案和下拉选中态出现一帧 OFF/未选中的闪烁。
    ;[gameEq.value, musicEq.value, movieEq.value].forEach(item => { item.enabled = false })
    activeMode.value = mode
    state.enabled = true
    state.currentPreset = presetId
    state.lastPreset = presetId
    appliedMode.value = mode
    appliedPresetId.value = presetId
    markPendingHardwareWrite(true, mode, presetId)
    if (persist) saveToStorage()
    if (syncLastPreset) scheduleSetLastPreset(mode, presetId)

    try {
      const { useChijiStore } = await import('./chiji')
      const chijiStore = useChijiStore()
      if (chijiStore.enabled) await chijiStore.setEnabled(false)
    } catch {
      // 状态仓库可以在测试中独立使用。
    }

    await enqueueDevice(async () => {
      await sendModeState(true, mode, presetId)
      if (hidManager.isConnected()) await delay(30)
      await sendCurve(preset.datapoint, false)
    })
  }

  async function setEqEnabled(enabled: boolean): Promise<void> {
    if (enabled) {
      await applyPreset(activeMode.value, currentModeState.value.currentPreset)
      return
    }

    const mode = appliedMode.value || activeMode.value
    ;[gameEq.value, musicEq.value, movieEq.value].forEach(item => { item.enabled = false })
    appliedMode.value = null
    appliedPresetId.value = null
    markPendingHardwareWrite(false, mode, null)
    saveToStorage()

    await enqueueDevice(async () => {
      await sendModeState(false, mode, null)
      if (hidManager.isConnected()) await delay(30)
      await sendCurve(createZeroCurve(), false)
    })
  }

  async function selectPreset(presetId: string): Promise<void> {
    await applyPreset(activeMode.value, presetId)
  }

  async function updateEqGain(bandIndex: number, gain: number, skipDeviceSend = false): Promise<void> {
    const preset = currentPreset.value
    const point = preset?.datapoint[bandIndex]
    if (!preset || !point || !slidersInteractive.value) return
    point.gain = Math.max(-12, Math.min(12, Math.round(gain * 2) / 2))

    if (!skipDeviceSend && hidManager.isConnected()) {
      const command = buildEqSinglePointCommand(bandIndex, point)
      void enqueueDevice(async () => {
        await sendLoginStatus()
        await hidManager.sendData(command, false)
      })
    }

    if (isLoggedIn() && !isDraftPreset(preset.id)) {
      debouncedSyncPointToApi(activeMode.value, preset.id, bandIndex, point)
    }
  }

  async function updateEqFrequency(bandIndex: number, frequency: string): Promise<void> {
    const preset = currentPreset.value
    const point = preset?.datapoint[bandIndex]
    if (!preset || !point || !slidersInteractive.value) return
    point.freq = frequency
    await updateEqGain(bandIndex, point.gain)
  }

  function debouncedSyncPointToApi(mode: EqMode, presetId: string, bandIndex: number, point: EqDataPoint) {
    const key = `${mode}-${presetId}-${bandIndex}`
    if (apiDebounceTimers.value[key]) clearTimeout(apiDebounceTimers.value[key])
    apiDebounceTimers.value[key] = setTimeout(async () => {
      const uuid = getUuid()
      if (!uuid) return
      try {
        await updateEqPoint(mode, uuid, presetId, point, getLinktype())
      } catch (error) {
        console.error('[EQ] 同步频点失败:', error)
      }
      delete apiDebounceTimers.value[key]
    }, 500)
  }

  function scheduleSetLastPreset(mode: EqMode, presetId: string): void {
    if (!isLoggedIn()) return
    const key = `${mode}-${presetId}`
    if (lastPresetTimers.value[key]) clearTimeout(lastPresetTimers.value[key])
    lastPresetTimers.value[key] = setTimeout(async () => {
      try {
        await setLastEqPreset(mode, getUuid(), presetId, getLinktype())
      } catch (error) {
        console.error('[EQ] 保存上次预设失败:', error)
      }
      delete lastPresetTimers.value[key]
    }, 200)
  }

  async function applyCurrentEq(): Promise<void> {
    const preset = currentPreset.value
    if (!preset) return
    await enqueueDevice(() => sendCurve(preset.datapoint))
  }

  function createCustomPresetDraft(name: string, datapoint: EqDataPoint[]): string | null {
    // 锁定点击新增时所在模式；草稿立即进入 UI，不等待后端。
    const mode = activeMode.value
    const state = getModeState(mode)
    if (state.customPresets.length >= MAX_CUSTOM_PRESETS) return null

    const presetId = `custom-${mode}-${Date.now()}`
    const newPreset: EqPresetData = {
      id: presetId,
      name: name.trim(),
      datapoint: clonePoints(datapoint),
      isCustom: true,
      createdAt: Date.now(),
    }

    state.customPresets.push(newPreset)
    draftPresetIds.add(presetId)

    // applyPreset 在第一个 await 之前就会完成选中态更新，因此标签会在本次点击内立即变蓝。
    // 草稿阶段不写 localStorage / setLast，避免临时 id 被保存或发给服务器。
    void applyPreset(mode, presetId, { persist: false, syncLastPreset: false })
    return presetId
  }

  async function finalizeCustomPresetDraft(presetId: string, name: string): Promise<boolean> {
    const location = findCustomPresetLocation(presetId)
    if (!location || !draftPresetIds.has(presetId)) return false

    const { mode, state, preset } = location
    const normalizedName = name.trim().slice(0, 20)
    if (!normalizedName) return false
    if (presetsForMode(mode).some(item => item.id !== presetId && item.name.trim().toLowerCase() === normalizedName.toLowerCase())) {
      return false
    }

    preset.name = normalizedName
    const oldId = preset.id

    if (isLoggedIn()) {
      try {
        const response = await addEqPreset(mode, getUuid(), preset, getLinktype())
        // 与旧版接口语义保持一致：code === 1 就代表新增成功。
        // 部分环境的 addEqData 成功响应并不会返回 data.id；上一版把“没有 id”误判成失败，
        // 结果就是服务器已经创建成功、前端却连续弹 Error，并且再次操作时制造重复标签。
        if (Number(response.code) !== 1) return false

        const responseData = response.data as unknown
        let returnedId: string | null = null
        if (typeof responseData === 'string' || typeof responseData === 'number') {
          returnedId = String(responseData)
        } else if (responseData && typeof responseData === 'object') {
          const data = responseData as Record<string, unknown>
          const candidate = data.id ?? data.presetId ?? data.eqId
          if (typeof candidate === 'string' || typeof candidate === 'number') returnedId = String(candidate)
        }

        // 后端没有回传 id 时继续沿用提交给 addEqData 的客户端 id（旧版就是这个行为）。
        // draft 身份由 draftPresetIds 单独维护，因此 id 本身不需要承担“是否草稿”的语义。
        const remoteId = returnedId || oldId
        preset.id = remoteId
        draftPresetIds.delete(oldId)

        // 后端 id 返回后，把所有仍引用临时 id 的状态一次性替换掉，避免 UI/硬件状态断链。
        if (state.currentPreset === oldId) state.currentPreset = remoteId
        if (state.lastPreset === oldId) state.lastPreset = remoteId
        if (state.pinnedPreset === oldId) state.pinnedPreset = remoteId
        if (appliedMode.value === mode && appliedPresetId.value === oldId) appliedPresetId.value = remoteId
        if (pendingHardwareWrite?.mode === mode && pendingHardwareWrite.presetId === oldId) {
          pendingHardwareWrite.presetId = remoteId
        }

        saveToStorage()
        if (appliedMode.value === mode && appliedPresetId.value === remoteId) {
          scheduleSetLastPreset(mode, remoteId)
        }
        return true
      } catch (error) {
        console.error('[EQ] 保存新增预设失败:', error)
        return false
      }
    }

    draftPresetIds.delete(oldId)
    saveToStorage()
    return true
  }

  async function addCustomPreset(name: string, datapoint: EqDataPoint[]): Promise<string | null> {
    // Save As / 对话框仍走“确认即保存”；内部复用同一套草稿保存逻辑，保证新增语义一致。
    if (currentModeState.value.customPresets.length >= MAX_CUSTOM_PRESETS) throw new Error('CUSTOM_PRESET_LIMIT')
    const presetId = createCustomPresetDraft(name, datapoint)
    if (!presetId) throw new Error('CUSTOM_PRESET_LIMIT')
    const success = await finalizeCustomPresetDraft(presetId, name)
    if (!success) {
      const location = findCustomPresetLocation(presetId)
      if (location) {
        const index = location.state.customPresets.findIndex(item => item.id === presetId)
        if (index >= 0) location.state.customPresets.splice(index, 1)
      }
      draftPresetIds.delete(presetId)
      throw new Error('ADD_FAILED')
    }
    return currentModeState.value.currentPreset
  }

  async function deleteCustomPreset(presetId: string): Promise<boolean> {
    const location = findCustomPresetLocation(presetId)
    if (!location) return false
    const { mode, state } = location
    const index = state.customPresets.findIndex(preset => preset.id === presetId)
    if (index < 0) return false

    const draft = draftPresetIds.has(presetId)
    if (!draft && isLoggedIn()) {
      try {
        const response = await removeEqPreset(mode, getUuid(), presetId, getLinktype())
        if (Number(response.code) !== 1) return false
      } catch (error) {
        console.error('[EQ] 删除预设失败:', error)
        return false
      }
    }

    state.customPresets.splice(index, 1)
    draftPresetIds.delete(presetId)
    const fallback = state.systemPresets[0]?.id || ''
    state.currentPreset = fallback
    if (appliedMode.value === mode && appliedPresetId.value === presetId && fallback) {
      await applyPreset(mode, fallback)
    }
    saveToStorage()
    return true
  }

  async function renameCustomPreset(presetId: string, name: string): Promise<boolean> {
    if (draftPresetIds.has(presetId)) {
      return finalizeCustomPresetDraft(presetId, name)
    }

    const location = findCustomPresetLocation(presetId)
    if (!location) return false
    const { mode, preset } = location
    const normalizedName = name.trim().slice(0, 20)
    if (!normalizedName) return false
    if (presetsForMode(mode).some(item => item.id !== presetId && item.name.toLowerCase() === normalizedName.toLowerCase())) {
      return false
    }
    if (isLoggedIn()) {
      try {
        const response = await renameEqPreset(mode, getUuid(), presetId, normalizedName, getLinktype())
        if (Number(response.code) !== 1) return false
      } catch (error) {
        console.error('[EQ] 重命名预设失败:', error)
        return false
      }
    }
    preset.name = normalizedName
    saveToStorage()
    return true
  }

  async function resetCurrentPreset(): Promise<boolean> {
    const preset = currentPreset.value
    if (!preset || preset.isCustom) return false
    const source = SYSTEM_PRESETS[activeMode.value].find(item => item.id === preset.id)
    if (!source) return false
    if (isLoggedIn()) {
      try {
        const response = await resetEqPreset(activeMode.value, getUuid(), preset.id, getLinktype())
        if (Number(response.code) !== 1) return false
      } catch (error) {
        console.error('[EQ] 重置预设失败:', error)
        return false
      }
    }
    preset.datapoint = clonePoints(source.datapoint)
    if (slidersInteractive.value) await applyCurrentEq()
    return true
  }

  async function pinPreset(presetId: string): Promise<boolean> {
    // 置顶首先是一个 UI 排序动作：点击后标签必须立即移动到第一位，
    // 不能等接口返回后才变化。接口失败时再恢复之前的置顶状态。
    const mode = activeMode.value
    const state = getModeState(mode)
    if (!presetsForMode(mode).some(preset => preset.id === presetId)) return false

    const previousPinned = state.pinnedPreset
    state.pinnedPreset = presetId

    if (isLoggedIn()) {
      try {
        const response = await pinEqPreset(mode, getUuid(), presetId, getLinktype())
        if (Number(response.code) !== 1) {
          state.pinnedPreset = previousPinned
          return false
        }
      } catch (error) {
        console.error('[EQ] 置顶预设失败:', error)
        state.pinnedPreset = previousPinned
        return false
      }
    }

    saveToStorage()
    return true
  }

  async function loadFromRemote(mode: EqMode): Promise<boolean> {
    if (!isLoggedIn() || remoteLoaded.value[mode]) return false
    const state = getModeState(mode)
    try {
      const response = await getEqList(mode, getUuid(), getLinktype())
      if (Number(response.code) !== 1 || !response.data) return false
      const presets = response.data.presets.map(normalizePreset)
      const remoteSystem = presets.filter(preset => SYSTEM_PRESET_IDS[mode].includes(preset.id))
      const remoteCustom = presets.filter(preset => !SYSTEM_PRESET_IDS[mode].includes(preset.id))
      if (remoteSystem.length) state.systemPresets = remoteSystem
      const localDrafts = state.customPresets.filter(preset => draftPresetIds.has(preset.id))
      state.customPresets = [
        ...remoteCustom.map((preset, index) => ({
          ...preset,
          isCustom: true,
          createdAt: preset.createdAt || Date.now() + index,
        })),
        ...localDrafts,
      ]
      state.lastPreset = response.data.lastPreset || null
      state.pinnedPreset = response.data.defaultPreset || null
      if (state.lastPreset && presetsForMode(mode).some(preset => preset.id === state.lastPreset)) {
        state.currentPreset = state.lastPreset
      }
      remoteLoaded.value[mode] = true
      return true
    } catch (error) {
      console.error(`[EQ] 加载 ${mode} 模式预设失败:`, error)
      return false
    }
  }

  async function loadAllFromRemote(): Promise<void> {
    if (!isLoggedIn()) return
    await Promise.all([loadFromRemote('game'), loadFromRemote('music'), loadFromRemote('movie')])
  }

  function syncHardwareStatus(enabled: boolean, modeValue: number, presetIndex: number): void {
    if (isLoggedIn()) return
    const mode = HARDWARE_TO_MODE[Math.max(0, Math.min(2, modeValue))] || 'game'
    const state = getModeState(mode)
    const preset = presetsForMode(mode)[Math.max(0, presetIndex)] || state.systemPresets[0]

    if (pendingHardwareWrite) {
      const expected = pendingHardwareWrite
      const confirmed = enabled === expected.enabled
        && (!enabled || (mode === expected.mode && preset.id === expected.presetId))

      if (confirmed) {
        pendingHardwareWrite = null
      } else if (Date.now() < expected.expiresAt) {
        // 这是写命令之后到达的旧 0x80 状态，不允许它把刚选中的 UI 顶回去。
        return
      } else {
        // 超时后恢复以硬件真实状态为准，避免设备写入失败时 UI 永久停在乐观值。
        pendingHardwareWrite = null
      }
    }

    ;[gameEq.value, musicEq.value, movieEq.value].forEach(item => { item.enabled = false })
    if (!hardwareStatusInitialized) {
      activeMode.value = mode
      hardwareStatusInitialized = true
    }
    state.currentPreset = preset.id
    state.enabled = enabled
    appliedMode.value = enabled ? mode : null
    appliedPresetId.value = enabled ? preset.id : null
  }

  function loadFromStorage(): void {
    if (!isLoggedIn()) return
    try {
      const raw = localStorage.getItem('eqSettings')
      if (!raw) return
      const saved = JSON.parse(raw)
      if (['game', 'music', 'movie'].includes(saved.activeMode)) activeMode.value = saved.activeMode
      for (const mode of ['game', 'music', 'movie'] as EqMode[]) {
        const target = getModeState(mode)
        const source = saved.modes?.[mode]
        if (!source) continue
        if (Array.isArray(source.customPresets)) target.customPresets = source.customPresets.map(normalizePreset)
        if (source.currentPreset && presetsForMode(mode).some(preset => preset.id === source.currentPreset)) {
          target.currentPreset = source.currentPreset
        }
        target.pinnedPreset = source.pinnedPreset || null
        target.lastPreset = source.lastPreset || null
      }
    } catch (error) {
      console.warn('[EQ] 加载本地状态失败:', error)
    }
  }

  function saveToStorage(): void {
    if (!isLoggedIn()) return
    try {
      const modes = Object.fromEntries((['game', 'music', 'movie'] as EqMode[]).map(mode => {
        const state = getModeState(mode)
        const currentPreset = draftPresetIds.has(state.currentPreset)
          ? (state.systemPresets[0]?.id || '')
          : state.currentPreset
        const lastPreset = state.lastPreset && !draftPresetIds.has(state.lastPreset)
          ? state.lastPreset
          : null
        const pinnedPreset = state.pinnedPreset && !draftPresetIds.has(state.pinnedPreset)
          ? state.pinnedPreset
          : null
        return [mode, {
          currentPreset,
          customPresets: state.customPresets.filter(preset => !draftPresetIds.has(preset.id)),
          lastPreset,
          pinnedPreset,
        }]
      }))
      localStorage.setItem('eqSettings', JSON.stringify({ activeMode: activeMode.value, modes }))
    } catch (error) {
      console.warn('[EQ] 保存本地状态失败:', error)
    }
  }

  watch(() => userStore.isLoggedIn, loggedIn => {
    if (loggedIn) {
      loadFromStorage()
      void loadAllFromRemote()
      return
    }
    gameEq.value = createModeState('game')
    musicEq.value = createModeState('music')
    movieEq.value = createModeState('movie')
    activeMode.value = 'game'
    appliedMode.value = null
    appliedPresetId.value = null
    pendingHardwareWrite = null
    hardwareStatusInitialized = false
    draftPresetIds.clear()
    remoteLoaded.value = { game: false, music: false, movie: false }
    localStorage.removeItem('eqSettings')
  })

  return {
    gameEq,
    musicEq,
    movieEq,
    activeMode,
    appliedMode,
    appliedPresetId,
    remoteLoaded,
    currentModeState,
    currentSettings,
    allPresets,
    currentPreset,
    currentPresetData,
    isSystemPreset,
    canAddMoreCustom,
    isEnabled,
    currentMode,
    slidersInteractive,
    setActiveMode,
    switchMode,
    setEqEnabled,
    selectPreset,
    updateEqGain,
    updateEqFrequency,
    applyCurrentEq,
    createCustomPresetDraft,
    finalizeCustomPresetDraft,
    isDraftPreset,
    addCustomPreset,
    deleteCustomPreset,
    renameCustomPreset,
    resetCurrentPreset,
    pinPreset,
    loadFromRemote,
    loadAllFromRemote,
    syncHardwareStatus,
    loadFromStorage,
    saveToStorage,
  }
})
